import type {
  CheckInPreview,
  EmployeeConfig,
  EmployeeDay,
  EmployeeSession,
  ScheduleDay,
} from "./employee-api";

/**
 * What the portal's Today page says about the employee's day (FEAT-2026-024).
 *
 * Kept pure so the rules can be tested without a browser, and so the page has
 * one place to get them wrong. The page used to decide everything from "is a
 * session open?", which made a finished day read "Not checked in" and offer a
 * Check in the server refused (BUG-2026-013).
 */

/** Where the day stands, read from its sessions (BR-159). */
export type DayPhase = "not-started" | "checked-in" | "checked-out";

export interface TodaySummary {
  phase: DayPhase;
  /** The open session — today's, or an overnight one still open from yesterday. */
  openSession: EmployeeSession | null;
  /** False for an overnight session, which belongs to yesterday's record. */
  openSessionIsToday: boolean;
  firstInAt: string | null;
  /** Null while a session is open: the day has no last check-out yet. */
  lastOutAt: string | null;
  /** Today's recorded minutes, plus the running session when it is today's — as the app counts. */
  workedMinutes: number;
  /** The day closed short of its hours or before its end (BR-155). */
  isShort: boolean;
  shortByMinutes: number | null;
  leftEarlyByMinutes: number | null;
}

/**
 * Summarise today from its sessions — never from the status column alone.
 *
 * An admin flag writes PENDING_REVIEW over the status while the sessions stay
 * as they were; the app learned that as mobile BUG-2026-009. `current` is what
 * `/mobile/attendance/current` returns: the open session on *any* date, which is
 * how a night shift that started yesterday still reads as checked in.
 */
export function summariseToday(
  day: EmployeeDay | null,
  current: EmployeeSession | null,
  now: Date,
): TodaySummary {
  const sessions = day?.sessions ?? [];
  const openToday = sessions.find((s) => !s.endAt) ?? null;
  const openSession = openToday ?? current ?? null;
  const openSessionIsToday =
    openSession != null && sessions.some((s) => s.id === openSession.id);

  const phase: DayPhase = openSession
    ? "checked-in"
    : sessions.length > 0
      ? "checked-out"
      : "not-started";

  const running =
    openSession && openSessionIsToday
      ? Math.max(0, Math.floor((now.getTime() - Date.parse(openSession.startAt)) / 60_000))
      : 0;

  const lastEnd = sessions.reduce<string | null>(
    (latest, s) => (s.endAt && (!latest || s.endAt > latest) ? s.endAt : latest),
    null,
  );

  const shortByMinutes = day?.shortByMinutes ?? null;
  const leftEarlyByMinutes = day?.leftEarlyByMinutes ?? null;

  return {
    phase,
    openSession,
    openSessionIsToday,
    firstInAt: day?.checkInAt ?? sessions[0]?.startAt ?? openSession?.startAt ?? null,
    lastOutAt: phase === "checked-out" ? (day?.checkOutAt ?? lastEnd) : null,
    workedMinutes: (day?.totalDurationMinutes ?? 0) + running,
    // Null is "not evaluated", never "short" (BR-155).
    isShort:
      phase === "checked-out" && ((shortByMinutes ?? 0) > 0 || (leftEarlyByMinutes ?? 0) > 0),
    shortByMinutes,
    leftEarlyByMinutes,
  };
}

export type TodayAction = "check-in" | "check-out" | "check-in-again" | "done";

/**
 * What the button offers. A finished day on a one-session organisation offers
 * nothing: the server refuses a second check-in there (BR-021).
 */
export function nextAction(
  phase: DayPhase,
  mode: EmployeeConfig["attendance"]["mode"],
): TodayAction {
  if (phase === "checked-in") return "check-out";
  if (phase === "not-started") return "check-in";
  return mode === "MULTIPLE_SESSIONS_PER_DAY" ? "check-in-again" : "done";
}

export function statusLabel(summary: TodaySummary): string {
  switch (summary.phase) {
    case "checked-in":
      return "Checked in";
    case "checked-out":
      return summary.isShort ? "Partial day" : "Checked out";
    default:
      return "Not checked in";
  }
}

/**
 * Why a day is not scheduled, in the employee's words — or null for an
 * ordinary day off the working pattern, which needs no explanation.
 */
export function notScheduledReason(day: ScheduleDay): string | null {
  switch (day.reason) {
    case "holiday":
      return day.holidayName ?? "Public holiday";
    case "rostered-off":
      return "Day off";
    default:
      return null;
  }
}

/**
 * Today's hours, from the schedule — so a shift or roster entry sets them,
 * where the app's header shows the site's hours.
 *
 * A day that is not scheduled says so and never says check-in is closed: it
 * means attendance is not expected, and a check-in still records (BR-202).
 */
export function scheduleLabel(day: ScheduleDay | null | undefined): string | null {
  if (!day) return null;
  if (!day.working) {
    const reason = notScheduledReason(day);
    return reason ? `Not scheduled today · ${reason}` : "Not scheduled today";
  }
  const hours = day.startTime && day.endTime ? `${day.startTime} – ${day.endTime}` : null;
  const parts = [hours, day.shiftName].filter((p): p is string => !!p);
  return parts.length > 0 ? parts.join(" · ") : "Working today";
}

/** "±12 m", "±1.2 km"; empty when there is no figure. */
export function accuracyLabel(m: number | null): string {
  if (m == null) return "";
  if (!Number.isFinite(m)) return "unknown";
  return m >= 1000 ? `±${(m / 1000).toFixed(1)} km` : `±${Math.round(m)} m`;
}

function distanceLabel(m: number): string {
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
}

export interface LocationSummary {
  label: string;
  tone: "good" | "warn" | "info" | "neutral";
  detail: string;
}

/**
 * Where the server places the employee, from its own preview (BR-153) — never
 * from the page's own geofence maths, which cannot see WFH policy or a WFA
 * approval (the trap mobile BUG-2026-004 fell into).
 *
 * WFH and WFA come first: both are allowed zones outside the site, so the
 * site's "outside" would be true and beside the point.
 */
export function describeLocation(
  preview: CheckInPreview,
  kind: "check-in" | "check-out" = "check-in",
): LocationSummary {
  const site = preview.site?.name ?? "your work location";

  if (preview.workMode === "WFH") {
    return {
      label: "At home (WFH)",
      tone: "info",
      // How a check-in would count means nothing on the way out.
      detail:
        kind === "check-in" ? "A check-in here counts as work from home." : "At your registered home.",
    };
  }
  if (preview.workMode === "WFA") {
    return {
      label: "Approved WFA",
      tone: "info",
      detail: "Your approved Work From Anywhere application covers today.",
    };
  }
  if (preview.geofenceStatus === "NOT_APPLICABLE") {
    return preview.site
      ? { label: "No geofence", tone: "neutral", detail: `${site} doesn't check a geofence.` }
      : {
          label: "No work location",
          tone: "neutral",
          detail: "You have no work location, so there's no geofence to check.",
        };
  }
  // A fix too coarse to place anyone reads OUTSIDE (BR-152); saying "outside"
  // would be a claim the reading cannot support.
  if (preview.gpsStatus === "INVALID") {
    return {
      label: "Low accuracy",
      tone: "warn",
      detail: `Not precise enough to place you at ${site}.`,
    };
  }
  if (preview.geofenceStatus === "INSIDE") {
    return { label: "Inside", tone: "good", detail: `At ${site}.` };
  }
  return {
    label: "Outside",
    tone: "warn",
    detail:
      preview.distanceM != null
        ? `About ${distanceLabel(preview.distanceM)} from ${site}.`
        : `Outside ${site}.`,
  };
}

export interface CheckInVerdict {
  allowed: boolean;
  message: string;
}

/**
 * Would a check-in here be accepted? Only asked before a check-in: while
 * checked in, the next punch is a check-out, which location never blocks.
 *
 * Refusals about the day ("already checked in") are the status card's to
 * explain; this answers for the place only.
 */
export function checkInVerdict(
  preview: CheckInPreview,
  action: TodayAction,
): CheckInVerdict | null {
  if (action !== "check-in" && action !== "check-in-again") return null;
  const refusal = preview.refusal;
  if (!refusal) {
    // Accepted but too coarse to place anyone: the GPS-only fallback is on, so
    // the server records it and marks it (BR-152). Say so — never "may be
    // rejected", the portal's old wording for a limit of its own.
    return preview.gpsStatus === "INVALID"
      ? { allowed: true, message: "You can check in here — it will be marked low accuracy." }
      : { allowed: true, message: "You can check in here." };
  }
  if (refusal.code === "OUTSIDE_GEOFENCE" || refusal.code === "IMPRECISE_LOCATION") {
    return { allowed: false, message: refusal.message };
  }
  return null;
}

/**
 * How far short today would close if the open session ended now (BR-155), or
 * null — when not checked in, when no minimum is set (never zero: "not set" is
 * not "met"), or for a night shift from yesterday, which the server judges
 * against its own day.
 */
export function projectedShortfall(day: EmployeeDay | null, summary: TodaySummary): number | null {
  if (summary.phase !== "checked-in" || !summary.openSessionIsToday) return null;
  if (day?.expectedMinutes == null) return null;
  const shortBy = day.expectedMinutes - summary.workedMinutes;
  return shortBy > 0 ? shortBy : null;
}

/** "Saturday, 26 September" for a YYYY-MM-DD date, which carries no time zone. */
export function dayHeading(isoDate: string): string {
  return new Intl.DateTimeFormat(undefined, {
    timeZone: "UTC",
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date(`${isoDate}T00:00:00.000Z`));
}
